import React, { useState, useEffect } from "react";
import { Space, Row, Layout, message, Col } from "antd";
import { SearchOutlined, MoreOutlined } from "@ant-design/icons";
import TableWithLoadingWrapper from "../../../components/generic/dataGrid";
import EditGeneralLedgerForm from "./editGeneralLedgerForm";
import AddGeneralLedgerForm from "./addGeneralLedgerForm";
import TitledSection from "../../../components/generic/titledSection";
import { backendDeleteWithoutRefresh } from "../../../helpers/callBackend";
import GenericDropdown from "../../../components/generic/genericDropdown";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import GenericDeleteModal from "../../../components/buttons/genericDeleteModal";
import HideOnMobile from "../../../components/generic/hideOnMobile";
import NewGeneralLedgerRule from "../../../components/specific/generalLedger/newGeneralLedgerRule";
import EditGeneralLedgerRule from "../../../components/specific/generalLedger/editGeneralLedgerRule";
import { Link } from "react-router-dom";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import PermissionGate from '../../../helpers/PermissionGate'
import Button from '@mui/material/Button'

const GeneralLedgerRules = (props) => {
  const [generalLedgerRules, setGeneralLedgerRules] = useState([]);
  const [gLRulesPageCountExpected, setGLRulesPageCountExpected] = useState(-1);
  const [gLRulesPageCountReceived, setGLRulesPageCountReceived] = useState(0);
  const [gLRulesloading, setGLRulesLoading] = useState(true);
  const [groupingType, setGroupingType] = useState(null);
  const [selectGroup, setSelectGroup] = useState(null);
  const [selectGroupFilter, setSelectGroupFilter] = useState([]);
  const [groupingOptionsCache, setGroupingOptionsCache] = useState({});
  const [loadingGroupingOptions, setLoadingGroupingOptions] = useState({ 'BA': true, 'EN': true, 'AA': true });
  
  const supportedGroupingTypes = ['BA', 'EN', 'AA'];
  const currentCompanyName = localStorage.getItem("arpariAffiliatedCompany")?.company_name || "";

  const groupingTypeFilter = [
    { label: "Bank", value: "BA" },
    { label: "Entity", value: "EN" },
    { label: "All Accounts", value: "AA" },
  ];

  const groupKeyToLabelMap = {
    EN: "Entity",
    BA: "Bank",
    AA: "All Accounts",
  };

  useEffect(() => {
    if (gLRulesPageCountExpected === -1) return;
    setGLRulesLoading(gLRulesPageCountExpected > gLRulesPageCountReceived);
  }, [gLRulesPageCountExpected, gLRulesPageCountReceived]);

  useEffect(() => {
    const params = { page_index: 0 };
    backendGetRequest("/api/general-ledger/rules/", { params: params })
      .then((res) => {
        setGLRulesPageCountExpected(res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          params.page_index = i;
          backendGetRequest("/api/general-ledger/rules/", { params: params })
            .then((res) => {
              setGLRulesPageCountReceived(prevState => prevState + 1);
              setGeneralLedgerRules(prevState => [...prevState, ...res?.data?.rules]);
            })
            .catch((res) => {
              setGLRulesPageCountExpected(prevState => prevState - 1);
            })
        }
      });
  }, []);

  useEffect(() => {
    if (!groupingType) {
      setSelectGroupFilter([]);
      setSelectGroup(null);
      return;
    }
    if (groupingOptionsCache[groupingType]) {
      setSelectGroupFilter(groupingOptionsCache[groupingType]);
    } else {
      setSelectGroupFilter([]);
    }
    
    setSelectGroup(null);
  }, [groupingType, groupingOptionsCache]);

  useEffect(() => {
    supportedGroupingTypes.map((type) => {
      if (!groupingOptionsCache[type]) {
        setLoadingGroupingOptions(prev => ({ ...prev, [type]: true }));
        try {
          backendGetRequestWithCache(
            "/api/landlord/grouping-type-filter-options/",
            { params: { grouping_type: type } },
            (cachedResponse) => {
              if (cachedResponse?.data?.data && !groupingOptionsCache[type]) {
                setGroupingOptionsCache(prev => ({ ...prev, [type]: cachedResponse.data.data }));
              }
            },
            (nonCachedResponse) => {
              if (nonCachedResponse?.data?.data) {
                setGroupingOptionsCache(prev => ({ ...prev, [type]: nonCachedResponse.data.data }));
              }
            },
            (error) => {
              console.error(`Error preloading grouping options for ${type}:`, error);
            }
          );
        } catch (error) {
          console.error(`Error preloading grouping options for ${type}:`, error);
        } finally {
          setLoadingGroupingOptions(prev => ({ ...prev, [type]: false }));
        }
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      setLoadingGroupingOptions({});
    };
  }, []);

  const addNewGeneralLedgerRule = (newGeneralLedgerRule) => {
    setGeneralLedgerRules(prevState => [...prevState, newGeneralLedgerRule]);
  }

  const handleDelete = (deletedGeneralLedgerRuleId) => {
    setGeneralLedgerRules(prevState =>
      prevState.filter(rule => rule.id !== deletedGeneralLedgerRuleId)
    );
  }

  const handleEdit = (updatedGeneralLedgerRule) => {
    setGeneralLedgerRules(prevState =>
      prevState.map(rule =>
        rule.id === updatedGeneralLedgerRule?.id ? updatedGeneralLedgerRule : rule
      )
    );
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => (
        <Link to={`/general-ledger/rule/details/000${params?.row?.id}`}>{params?.value}</Link>
      ),
    },
    {
      field: 'gl_tag',
      headerName: 'G/L Tag',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
    },
    {
      field: 'grouping_type',
      headerName: 'Group Type/Grouping',
      flex: 1,
      valueGetter: (params) => groupKeyToLabelMap[params],
      renderCell: (params) =>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div style={{ fontSize: '1rem', color: '#888', marginRight: '10px' }}>
            {groupKeyToLabelMap[params?.row?.grouping_type]}
          </div>
          <div style={{ marginBottom: 12.5 }}>
            {params?.row?.group_name}
          </div>
        </div>
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.1,
      align: 'right',
      renderCell: (params) => {
        return (
          <PermissionGate requiredRole='dashboardEditor'>
            <GenericDropdown
              dropdownItems={[
                <>
                  <EditGeneralLedgerRule
                    onSuccess={handleEdit}
                    title={"Edit General Ledger Rule " + params?.row?.name}
                    glRuleId={params?.row?.id}
                    currentName={params?.row?.name}
                    currentDescription={params?.row?.description}
                    currentSearchTerm={params?.row?.search_term}
                    currentGroupingType={params?.row?.grouping_type}
                    currentGroupId={params?.row?.group_id}
                    currentGroupName={params?.row?.group_name}
                    currentGlAccountId={params?.row?.gl_account_id}
                    currentCustomStartDate={params?.row?.custom_start_date}
                    currentCustomEndDate={params?.row?.custom_end_date}
                    setSelectGroupFilter={setSelectGroupFilter}
                    selectGroupFilter={selectGroupFilter}
                    groupingTypeFilter={groupingTypeFilter}
                    setSelectGroup={setSelectGroup}
                    setGroupingType={setGroupingType}
                    groupingType={groupingType}
                    selectGroup={selectGroup}
                    generalLedgerAccount={props?.generalLedgerAccount}
                    loadingGroupingOptions={!groupingType || loadingGroupingOptions[groupingType]}
                  />
                </>
                ,
                <GenericDeleteModal
                  type='General Ledger Rule'
                  title={`Are you sure you want to delete ${params?.row?.name}?`}
                  URL="/api/general-ledger/rules/"
                  deleteParams={{ rule_id: params?.row?.id }}
                  description={`By clicking Delete below you will no longer have the general ledger rule ${params?.row?.name} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
                  handleDeleteSuccess={() => handleDelete(params?.row?.id)}
                  handleDeleteFailure={() => message.error(`Failed to delete the general ledger rule ${params?.row?.name}`)}
                />
              ]}
            />
          </PermissionGate>
        );
      }
    }
  ];

  return (<>
    <HeaderBox
      title={"General Ledger Rules"}
      ignoreDocumentTitle={true}
      actions={
        <HideOnMobile>
          <PermissionGate
            toolTipText='Only admins and dashboard editors may add new general ledger rules'
            requiredRole='dashboardEditor'
            readOnly={
              <Button 
                sx={{ fontSize: 14, textTransform: 'none' }}
                fullWidth
                size="small"
                variant="contained"
                disabled
              >
                + Add New
              </Button>
            }
          >
            <Row>
              <NewGeneralLedgerRule
                title={"Add new General Ledger Rule for " + currentCompanyName}
                generalLedgerAccount={props?.generalLedgerAccount}
                loadingGeneralLedgerAccount={props?.loadingGeneralLedgerAccount}
                onSuccess={addNewGeneralLedgerRule}
                selectGroupFilter={selectGroupFilter}
                groupingTypeFilter={groupingTypeFilter}
                setSelectGroup={setSelectGroup}
                setGroupingType={setGroupingType}
                groupingType={groupingType}
                selectGroup={selectGroup}
                loadingGroupingOptions={!groupingType || loadingGroupingOptions[groupingType]}
              />
            </Row>
          </PermissionGate>
        </HideOnMobile>
      }
    />
    
    
    <Space direction="vertical" style={{ width: "100%", marginTop: 6 }}>
          <TableWithLoadingWrapper
            pagination={{
              paginationModel: { pageSize: generalLedgerRules?.length || 10, page: 0 },
            }}
            pageSizeOptions={[5, 10, 25]}
            columns={columns}
            dataSource={generalLedgerRules}
            loading={gLRulesloading}
            getRowId={row => row.id}
          />
        </Space>
        </>
  );
};

export default GeneralLedgerRules;
